import React, { useEffect, useState } from "react";
import { Alert, Button, CircularProgress, Drawer, IconButton, MenuItem, TextField } from "@mui/material";
import { Close, DeleteOutline, DownloadOutlined } from "@mui/icons-material";
import { PortfolioUpdateFund, PortfolioListPublicFundDocuments, PortfolioDownloadDocument } from "../../util/API/portfolionew";
import { PortfolioAdminBulkUpload, PortfolioDeleteDocuments } from "../../util/API/portfolio";

const successful = response => response?.status === "success" || String(response?.header?.callStatus ?? response?.data?.header?.callStatus ?? response?.payload?.callStatus) === "1";

export default function FundEditDrawer({ fund, userId, userPin, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", description: "", associated_video_url: "", visibility: "public" });
  const [documents, setDocuments] = useState([]);
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [removing, setRemoving] = useState(null);
  const loadDocuments = async id => {
    const response = await PortfolioListPublicFundDocuments(id);
    setDocuments(Array.isArray(response?.data) ? response.data : []);
  };
  useEffect(() => {
    if (!fund) return;
    let active = true;
    setForm({ name: fund.name || "", description: fund.description || "", associated_video_url: fund.videoUrl || "", visibility: fund.visibility || "public" });
    setFiles([]); setDocuments([]); setMessage(null); setRemoving(null); setLoading(true);
    PortfolioListPublicFundDocuments(fund.id).then(response => { if (active) setDocuments(Array.isArray(response?.data) ? response.data : []); })
      .catch(() => { if (active) setMessage({ severity: "error", text: "Unable to load fund documents. Reopen the editor to retry." }); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [fund]);
  const change = field => event => setForm(current => ({ ...current, [field]: event.target.value }));
  const save = async event => {
    event.preventDefault(); setBusy(true); setMessage(null);
    try {
      await PortfolioUpdateFund(fund.id, form);
      await onSaved();
      setMessage({ severity: "success", text: "Fund details saved." });
    } catch (error) { setMessage({ severity: "error", text: error.response?.data?.message || "Unable to save fund details." }); }
    finally { setBusy(false); }
  };
  const upload = async () => {
    setBusy(true); setMessage(null);
    const remaining = [];
    try {
      for (const file of files) {
        try {
          const response = await PortfolioAdminBulkUpload([{ file, ownerType: "Fund", ownerId: userId, fundId: fund.id, documentType: "Fund", displayName: file.name, accessRole: form.visibility }], userId, userPin);
          const result = response?.data?.results?.item || response?.results?.item;
          const rows = result ? (Array.isArray(result) ? result : [result]) : [];
          if (!successful(response) || rows.some(row => String(row.status) !== "1")) throw new Error("Upload failed");
        } catch { remaining.push(file); }
      }
      setFiles(remaining);
      await loadDocuments(fund.id);
      setMessage({ severity: remaining.length ? "warning" : "success", text: remaining.length ? `${remaining.length} uploads failed. Retry the remaining files.` : "Documents uploaded." });
    } catch { setMessage({ severity: "warning", text: "Uploads processed, but the document list could not refresh. Reopen the editor." }); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true); setMessage(null);
    try {
      const response = await PortfolioDeleteDocuments({ file_key: removing.file_key });
      if (!successful(response)) throw new Error("Delete failed");
      setDocuments(current => current.filter(item => item.id !== removing.id)); setRemoving(null);
      setMessage({ severity: "success", text: "Document deleted." });
    } catch { setMessage({ severity: "error", text: "Unable to delete this document." }); }
    finally { setBusy(false); }
  };
  const download = async document => {
    try {
      const response = await PortfolioDownloadDocument(document.id);
      if (!response?.data?.download_url) throw new Error();
      window.open(response.data.download_url.replace(/&amp;/g, "&"), "_blank", "noopener,noreferrer");
    } catch { setMessage({ severity: "error", text: "Unable to download this document." }); }
  };
  return <Drawer anchor="right" open={Boolean(fund)} onClose={() => !busy && onClose()} PaperProps={{ className: "yp-drawer yp-wide-drawer" }}>
    <div className="yp-drawer-body yp-fund-editor">
      <div className="yp-drawer-heading"><div><span>Edit fund</span><h2>{fund?.name}</h2><p>Update fund information and manage fund documents.</p></div><IconButton disabled={busy} aria-label="Close fund editor" onClick={onClose}><Close /></IconButton></div>
      {message && <Alert severity={message.severity}>{message.text}</Alert>}
      <form className="yp-form-stack" onSubmit={save}>
        <TextField required label="Fund name / title" value={form.name} onChange={change("name")} inputProps={{ maxLength: 255 }} disabled={busy} />
        <TextField multiline minRows={4} label="Description" value={form.description} onChange={change("description")} inputProps={{ maxLength: 10000 }} disabled={busy} />
        <TextField type="url" label="Presentation video URL" value={form.associated_video_url} onChange={change("associated_video_url")} helperText="Leave empty to remove the video." inputProps={{ maxLength: 255 }} disabled={busy} />
        <TextField select label="Fund visibility" value={form.visibility} onChange={change("visibility")} disabled={busy}><MenuItem value="public">Public</MenuItem><MenuItem value="private">Private</MenuItem></TextField>
        <Button type="submit" className="yp-primary" disabled={busy}>{busy ? "Please wait…" : "Save fund details"}</Button>
      </form>
      <section className="yp-fund-documents"><div className="yp-fund-documents-heading"><strong>Fund documents</strong><span>{documents.length} files</span></div>
        {loading ? <CircularProgress size={24} /> : <div className="yp-fund-document-list">{documents.map(document => <article key={document.id}><div><strong>{document.file_name}</strong><small>{document.access_role}</small></div><IconButton aria-label={`Download ${document.file_name}`} onClick={() => download(document)}><DownloadOutlined /></IconButton><IconButton disabled={busy} aria-label={`Delete ${document.file_name}`} onClick={() => setRemoving(document)}><DeleteOutline /></IconButton></article>)}{!documents.length && <p>No fund documents uploaded.</p>}</div>}
        {removing && <Alert severity="warning">Permanently delete “{removing.file_name}”?<div><Button disabled={busy} onClick={() => setRemoving(null)}>Cancel</Button><Button disabled={busy} onClick={remove}>Delete document</Button></div></Alert>}
        <label className="yp-file-input"><span>Add fund documents</span><input key={`${fund?.id}-${files.length}`} disabled={busy} type="file" multiple accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg" onChange={event => setFiles(Array.from(event.target.files || []))} /><small>New documents use the selected visibility. Existing document access is preserved.</small></label>
        {files.length > 0 && <><small>{files.map(file => file.name).join(", ")}</small><Button className="yp-primary" disabled={busy} onClick={upload}>Upload {files.length} documents</Button></>}
      </section>
      <div className="yp-drawer-actions"><Button disabled={busy} onClick={onClose}>Close</Button></div>
    </div>
  </Drawer>;
}
